import { useEffect, useRef } from 'react';
import { BEAT_EVENT_LIST, KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { selectActiveTab, selectIsEmptyData, useReportStore } from '@/entities/report';
import { HrChartHandle } from './hrChart';

/**
 * Assembles the shared state the rhythm-events tab wrapper hands down to the HR chart and strip panels. Reads the report info, ECG data map and the active event button from the store, derives whether that button is a beat view or a min/max HR view, clears the selected strip once the day turns out to hold no data, and forwards arrow-button clicks to the chart ref.
 *
 * @returns An object carrying the tab key, the empty-data flag, the active button, the report identifiers and ECG data map, the chart ref, the arrow-button handler and the beat and min/max HR mode flags.
 */
const useWrapReport = () => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;
  const isEmptyData = useReportStore(selectIsEmptyData(keyRecoil));
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Stores the strip currently selected in the grid. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);

  // reportInfoState (Recoil, Task 7) is typed {} | IReportInfo - cast to destructure.
  const { studyId, profileId, startReport, timezoneOffset, stopReport } = useReportStore(
    (s) => s.reportInfo,
  ) as any;
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);
  const hrChartRef = useRef<HrChartHandle>(null);

  /**
   * Steps the HR chart selection to the neighbouring event. Delegates to the chart's imperative handle when the chart is mounted.
   *
   * @param isNext - True to move forward, false to move back.
   * @returns Nothing.
   */
  const handleClickArrowButton = (isNext = false) => {
    if (hrChartRef.current) {
      hrChartRef.current.handleClickArrowButton(isNext);
    }
  };

  const isProcessBeatEvent = BEAT_EVENT_LIST.includes(activeButton as any);
  const isProcessMinMaxHr =
    activeButton === TYPE_RHYTHM_EVENT_ENUM.MAX_HR ||
    activeButton === TYPE_RHYTHM_EVENT_ENUM.MIN_HR;

  useEffect(() => {
    if (isEmptyData) {
      setSelectedStrip(null);
    }
  }, [isEmptyData]);

  return {
    keyRecoil,
    isEmptyData,
    activeButton,
    studyId,
    profileId,
    startReport,
    timezoneOffset,
    stopReport,
    ecgDataMap,
    hrChartRef,
    handleClickArrowButton,
    isProcessBeatEvent,
    isProcessMinMaxHr,
  };
};

export default useWrapReport;
